/** Shared formatting utilities for dates and activity log entries. */

// Every date this app shows a human is DD/MM/YYYY in UTC, fixed, never the
// viewer's environment.
//
// Locale: `toLocaleDateString()` without one follows the browser, so the same
// row read "6/10/2026" as 6 October to one colleague and 10 June to another,
// with nothing on screen to say which.
//
// Time zone: pinning only the locale fixes the order of the fields but not
// which day they name. An instant near midnight UTC renders as the previous
// day west of it, so two colleagues comparing the same row would still
// disagree. These dates are read side by side on the admin project list to
// decide what to delete, so one answer for everyone matters more than matching
// any one viewer's wall clock.
//
// 2-digit day and month keep table columns aligned.
const DATE_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "UTC",
});

// 24-hour, so "02:30" cannot be read as the afternoon, and UTC like the date.
// The zone is rendered with the value because a clock time that is not the
// viewer's own is misleading unless it says so.
const DATE_TIME_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

/** What an absolute date field shows when there is no date, or an unusable one. */
const NO_DATE = "-";

// What the relative format shows for a value it cannot read. Not `NO_DATE`,
// because this one is also read mid-sentence ("Logins checked ...") where a
// bare "-" is not English. Not "Never" either, which this file gives to an
// absent value and the project list treats as a verified claim: we asked
// inside the sweep window, and nobody has ever signed in. A timestamp we hold
// but cannot parse supports no such claim, and the page it feeds is the one
// whose buttons delete projects.
const UNKNOWN_TIME = "Unknown";

/**
 * Format an instant, or return the placeholder rather than throwing.
 *
 * `Intl.DateTimeFormat.format` raises `RangeError` on an unparseable date,
 * where the `toLocaleDateString` this replaced returned "Invalid Date". A
 * formatter that throws takes the whole table down with it, so a value we
 * cannot read renders as absent instead. The backend only emits ISO
 * timestamps, so this is a guard rather than an expected path.
 */
function format(formatter: Intl.DateTimeFormat, dateStr: string): string {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) {
    return NO_DATE;
  }
  return formatter.format(date);
}

export function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) {
    return "Never";
  }
  const date = new Date(dateStr);
  // An unparseable date makes every comparison below false, so without this it
  // would fall through to the absolute format and throw there instead.
  if (Number.isNaN(date.getTime())) {
    return UNKNOWN_TIME;
  }
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) {
    return "Just now";
  }
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) {
    return `${diffDays}d ago`;
  }
  return DATE_FORMAT.format(date);
}

export function formatDate(dateStr: string | null): string {
  if (!dateStr) {
    return NO_DATE;
  }
  return format(DATE_FORMAT, dateStr);
}

export function formatDateTime(dateStr: string | null): string {
  if (!dateStr) {
    return NO_DATE;
  }
  const formatted = format(DATE_TIME_FORMAT, dateStr);
  return formatted === NO_DATE ? formatted : `${formatted} UTC`;
}

export function formatActionLabel(action: string): string {
  return action.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatDetail(detail: Record<string, unknown>): string | null {
  const keys = Object.keys(detail);
  if (keys.length === 0) {
    return null;
  }

  return keys
    .map((key) => {
      const label = key.replace(/_/g, " ");
      return `${label}: ${String(detail[key])}`;
    })
    .join(" \u00b7 ");
}
